import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import * as Sentry from '@sentry/angular';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { register as registerSwiperElements } from 'swiper/element/bundle';
import { scrubLocation } from './app/shared/helpers/sentry-scrub';

// Dynamically load Calcite CSS to avoid @layer parsing issues
const link = document.createElement('link');
link.rel = 'stylesheet';
link.href = '/assets/calcite/calcite.css';
document.head.appendChild(link);

if (environment.production) {
  enableProdMode();
}

// Only initialize Sentry in non-development environments
if ((environment.APP_ENV as string) !== 'development') {
  Sentry.init({
    dsn: environment.SENTRY_DSN,
    environment: environment.SENTRY_ENVIRONMENT || environment.APP_ENV,
    release: `sitenotes-web@${environment.APP_VERSION}`,
    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          environment.API_ENDPOINT,
        ],
      }),
      // Replay network spans carry request URLs too (W32)
      Sentry.replayIntegration({ beforeAddRecordingEvent: event => scrubLocation(event) }),
    ],
    // "Sheets here" sent the position as ?at=lat,lon before W35 (now the X-Position header, which Sentry never records):
    // an old client's URL must still never reach Sentry (spec §16 rule 7, W32)
    beforeBreadcrumb: breadcrumb => scrubLocation(breadcrumb),
    beforeSend: event => scrubLocation(event),
    beforeSendTransaction: event => scrubLocation(event),
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

registerSwiperElements();

// Unregister any existing Service Workers to fix caching issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
    });
  });
}

// Safety net for stale HTML that references a bundle the server no longer
// has. Self-heals by clearing caches / unregistering any SW and doing a
// hard reload — so users are never trapped on a dead deploy.
let chunkReloadTriggered = false;
const handleChunkLoadFailure = async (details: string): Promise<void> => {
  if (chunkReloadTriggered) return;
  chunkReloadTriggered = true;
  console.warn('Chunk load failed, recovering with hard reload', details);
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map(r => r.unregister()));
    }
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map(k => caches.delete(k)));
    }
  } finally {
    window.location.reload();
  }
};
const isChunkLoadError = (msg: string): boolean =>
  /ChunkLoadError|Loading chunk [^\s]+ failed|Failed to fetch dynamically imported module/i.test(msg);
window.addEventListener('error', (event) => {
  const msg = `${event.message ?? ''} ${event.error?.stack ?? ''}`;
  if (isChunkLoadError(msg)) handleChunkLoadFailure(msg);
});
window.addEventListener('unhandledrejection', (event) => {
  const reason: unknown = event.reason;
  const msg = reason instanceof Error
    ? `${reason.message} ${reason.stack ?? ''}`
    : String(reason);
  if (isChunkLoadError(msg)) handleChunkLoadFailure(msg);
});

platformBrowserDynamic().bootstrapModule(AppModule)
  .catch(err => console.error(err));
